import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StarRating from '../Review/Questions/StarRating';
import TextAreaInput from '../common/TextAreaInput';
import { postRequest } from '../../requestUtils';
import './ConsultantReview.scss';

const instantiateRatings = (categories) => (
  categories.map((category) => ({ id: null, score: null, rating_category_id: category.id }))
);

const ratingsCount = (ratings) => (ratings.filter((r) => r.score).length);

const getAverageScore = (ratings) => {
  if (ratingsCount(ratings) === 0) return 0;
  const ratingSum = ratings.reduce((prev, curr) => prev + curr.score, 0);
  return (ratingSum / ratingsCount(ratings)).toFixed(1);
};

export default class ConsultantReview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ratings: instantiateRatings(props.ratingCategories),
      reviewText: '',
    };
    this.handleReviewSave = this.handleReviewSave.bind(this);
    this.handleUserInput = this.handleUserInput.bind(this);
    this.renderActionButton = this.renderActionButton.bind(this);
    this.handleAPISync = this.handleAPISync.bind(this);
    this.handleUpdateReview = this.handleUpdateReview.bind(this);
  }

  saveEnabled() {
    const { ratings, reviewText } = this.state;

    return (ratingsCount(ratings) > 0 && reviewText);
  }

  handleAPISync(path, params, token, fxn) {
    const { afterSubmitPath } = this.props;
    fxn(path, params, token).then(() => {
      window.location.href = afterSubmitPath;
    });
  }

  handleReviewSave() {
    const { ratings, reviewText } = this.state;
    const {
      csrfToken, consultantId, includeProjectParam, projectId,
    } = this.props;

    const saveReview = {
      reviewee_id: consultantId,
      recommend: true,
      private_review: reviewText,
      review_ratings_attributes: ratings.map((rating) => ({
        id: rating.id,
        rating_category_id: rating.rating_category_id,
        score: rating.score,
      })),
    };

    if (includeProjectParam) {
      Object.assign(saveReview, { project_id: projectId });
    }

    this.handleAPISync(
      '/consultant_reviews',
      { review: saveReview },
      csrfToken,
      postRequest,
    );
  }

  handleUserInput(type, value, id) {
    const { ratings } = this.state;
    if (type === 'ratings') {
      ratings.find((r) => r.rating_category_id === id).score = value;
      this.setState({ [type]: ratings });
    } else {
      this.setState({ [type]: value });
    }
  }

  handleUpdateReview(value) {
    this.setState({ reviewText: value });
  }

  renderActionButton() {
    return (
      <button
        id="review-save-feedback"
        className="btn btn-primary bigger-button full-width btn-review"
        onClick={this.handleReviewSave}
        disabled={!this.saveEnabled()}
      >
        Save Feedback
      </button>
    );
  }

  render() {
    const { ratings, reviewText } = this.state;
    const { consultantName, ratingCategories } = this.props;

    return (
      <div className="reviews-container style-guide">
        <div className="reviews-header g-row g-row-justify-right" />
        <h2 className="reviews-intro normal-weight">
          Leave internal feedback about
          {' '}
          <strong>{consultantName}</strong>
        </h2>
        <div className="mt-2 body-text-2 muted-gray-text">
          This is only visible to Clora internally.
          Consultants and Employers cannot see this.
        </div>

        <div className="reviews-question rating">
          <p className="mb-2">
            1. Rate
            {' '}
            <strong>{consultantName}</strong>
            <span className="text-danger">*</span>
          </p>
          <div className="mb-3 body-text-2 muted-gray-text">
            Indicate any applicable category.
          </div>
          <div className="ml-30">
            {ratingCategories.map(({ id, name }, idx) => (
              <StarRating
                id={id}
                name={name}
                edit
                handleUserInput={this.handleUserInput}
                rating={ratings[idx].score ? ratings[idx] : null}
              />
            ))}
            <div className="star-total demibold">
              Total Score:
              {getAverageScore(ratings)}
            </div>
          </div>
        </div>
        <div className="reviews-question recommendation">
          <p className="body-text-1 mb-3">
            2. Leave comment
            <span className="text-danger">*</span>
          </p>
          <TextAreaInput
            className="form-control fs-hide"
            placeholder="Type your review here..."
            textAreaPrompt=""
            rows={3}
            onAnswerChange={this.handleUpdateReview}
            value={reviewText}
          />
        </div>
        <div className="reviews-footer">
          {this.renderActionButton()}
        </div>
      </div>
    );
  }
}

ConsultantReview.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  consultantName: PropTypes.string.isRequired,
  consultantId: PropTypes.number.isRequired,
  ratingCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  afterSubmitPath: PropTypes.string.isRequired,
  projectId: PropTypes.number.isRequired,
  includeProjectParam: PropTypes.bool,
};

ConsultantReview.defaultProps = {
  includeProjectParam: false,
};
